import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import {
  AlertCircle,
  History,
  Info,
  KeyRound,
  Loader2,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Users
} from 'lucide-react';
import ConfirmDialog from './ConfirmDialog';
import { Toast } from './ui/Toast';
import { api } from '../services/api';

/**
 * ====================================
 * Double authentification
 * ====================================
 *
 * « Outils administratifs → Accès & sécurité → Double authentification ».
 *
 * L'administrateur n'enrôle pas le téléphone de l'employé : il EXIGE, et
 * l'employé scanne un QR code à sa prochaine connexion. Personne n'a besoin du
 * téléphone de l'autre, et le secret ne s'affiche que devant son porteur.
 *
 *   Non exigée  — le mot de passe suffit
 *   En attente  — exigée ; enrôlement à la prochaine connexion
 *   Active      — un code est demandé à chaque connexion
 *
 * Deux gestes sont refusés sur son propre compte — réinitialiser, désactiver —
 * pour qu'une session d'administrateur volée ne suffise pas à retirer la
 * protection. Le serveur le refuse aussi ; l'écran ne fait que le dire avant.
 *
 * La double authentification fait partie du socle de toutes les licences.
 */

const ETATS = {
  active: { libelle: 'Active', classes: 'bg-green-100 text-green-800' },
  en_attente: { libelle: 'En attente', classes: 'bg-amber-100 text-amber-900' },
  non_exigee: { libelle: 'Non exigée', classes: 'bg-gray-100 text-gray-600' }
};

const ACTIONS_JOURNAL = {
  exigee: 'Exigée',
  enrolement: 'Téléphone enrôlé',
  reinitialisee: 'Réinitialisée',
  desactivee: 'Désactivée',
  code_refuse: 'Code refusé',
  enrolement_refuse: 'Enrôlement refusé'
};

const SOURCES_JOURNAL = {
  ecole: 'École',
  cik: 'Support KLASYS',
  connexion: 'Connexion'
};

// DESIGN-SYSTEM.md : les boutons d'un pied de modal ont même hauteur et même
// largeur ; les boutons de section suivent le régime B.
const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';
const BOUTON_SECTION = 'inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-xs font-semibold transition-colors';
const BOUTON_LIGNE = 'h-8 w-[118px] justify-center gap-1.5 text-xs';

// « 2026-09-30T12:15:00Z » → « 30/09/2026 à 14:15 » pour qui regarde depuis la
// France, « 13:15 » depuis Brazzaville.
//
// Le serveur envoie l'instant en UTC ; c'est le navigateur qui le traduit, à
// l'heure de celui qui lit. Auparavant la valeur arrivait déjà découpée
// « 2026-09-30 12:15 » et s'affichait telle quelle : comme MySQL tourne en UTC,
// l'écran annonçait deux heures de retard, et une de moins encore l'hiver.
//
// L'ancien format est encore accepté — une réponse mise en cache par le
// navigateur, le temps d'une mise en production — et il est alors lu comme de
// l'UTC, ce qu'il a toujours été.
const jourEtHeure = (valeur) => {
  if (!valeur) return null;

  const texte = String(valeur).trim();
  const instant = /[TZ]/.test(texte)
    ? new Date(texte)
    : new Date(`${texte.replace(' ', 'T')}Z`);

  if (Number.isNaN(instant.getTime())) return null;

  const date = new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  }).format(instant);
  const heure = new Intl.DateTimeFormat('fr-FR', {
    hour: '2-digit', minute: '2-digit'
  }).format(instant);

  return `${date} à ${heure}`;
};

const nomComplet = (c) => `${c?.prenom || ''} ${c?.nom || ''}`.trim() || `Employé #${c?.id}`;

const Tuile = ({ libelle, valeur, icone: Icone, ton }) => (
  <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4">
    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${ton}`}>
      <Icone size={18} />
    </div>
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-500">{libelle}</p>
      <p className="text-2xl font-semibold tabular-nums text-gray-900">{valeur}</p>
    </div>
  </div>
);

const DoubleAuthManager = () => {
  const [comptes, setComptes] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [moi, setMoi] = useState(null);
  const [journal, setJournal] = useState([]);

  const [chargement, setChargement] = useState(true);
  const [erreurChargement, setErreurChargement] = useState(null);
  const [erreurJournal, setErreurJournal] = useState(null);

  const [recherche, setRecherche] = useState('');
  const [filtreEtat, setFiltreEtat] = useState('all');
  const [filtreGroupe, setFiltreGroupe] = useState('all');

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const avertir = (message, type = 'success') => setToast({ show: true, message, type });

  // Exiger pour un compte : confirmation simple, le geste se défait.
  const [aExiger, setAExiger] = useState(null);
  const [exigenceEnCours, setExigenceEnCours] = useState(false);

  // Exiger pour un groupe
  const [dialogueGroupe, setDialogueGroupe] = useState(false);
  const [groupeChoisi, setGroupeChoisi] = useState('');

  // Réinitialiser ou désactiver : motif obligatoire
  const [retrait, setRetrait] = useState(null); // { compte, action }
  const [motif, setMotif] = useState('');
  const [retraitEnCours, setRetraitEnCours] = useState(false);

  // ------------------------------------------------------------ chargement

  const charger = useCallback(async () => {
    setChargement(true);
    const [reponse, reponseJournal] = await Promise.all([
      api.doubleAuth.lister(),
      api.doubleAuth.journal()
    ]);

    // Une lecture ratée ne s'affiche jamais comme une liste vide : elle
    // laisserait croire qu'aucun compte n'est protégé.
    if (reponse?.success) {
      setComptes(reponse.data.comptes || []);
      setGroupes(reponse.data.groupes || []);
      setMoi(reponse.data.moi || null);
      setErreurChargement(null);
    } else {
      setErreurChargement(reponse?.message || 'Lecture des comptes impossible');
    }

    if (reponseJournal?.success) {
      setJournal(reponseJournal.data || []);
      setErreurJournal(null);
    } else {
      setErreurJournal(reponseJournal?.message || 'Lecture du journal impossible');
    }

    setChargement(false);
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  // ------------------------------------------------------------ dérivés

  const compteurs = useMemo(() => ({
    total: comptes.length,
    active: comptes.filter((c) => c.etat === 'active').length,
    en_attente: comptes.filter((c) => c.etat === 'en_attente').length,
    non_exigee: comptes.filter((c) => c.etat === 'non_exigee').length
  }), [comptes]);

  const comptesFiltres = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return comptes.filter((c) => {
      if (filtreEtat !== 'all' && c.etat !== filtreEtat) return false;
      if (filtreGroupe !== 'all' && String(c.groupe_id) !== filtreGroupe) return false;
      if (!terme) return true;
      return `${c.prenom || ''} ${c.nom || ''} ${c.identifiant || ''} ${c.role_nom || ''}`
        .toLowerCase().includes(terme);
    });
  }, [comptes, recherche, filtreEtat, filtreGroupe]);

  // Aperçu du groupe choisi. Le serveur retient TOUS les rôles de l'employé,
  // l'écran ne connaît que le groupe principal : l'aperçu peut en compter un
  // peu moins, et le résultat renvoyé dit exactement ce qui a été fait.
  const apercuGroupe = useMemo(() => {
    if (!groupeChoisi) return null;
    const membres = comptes.filter((c) => String(c.groupe_id) === groupeChoisi);
    return {
      membres,
      aExiger: membres.filter((c) => c.etat === 'non_exigee' && !c.poste_de_pointage).length,
      deja: membres.filter((c) => c.etat !== 'non_exigee').length,
      postes: membres.filter((c) => c.etat === 'non_exigee' && c.poste_de_pointage).length
    };
  }, [groupeChoisi, comptes]);

  // ------------------------------------------------------------ actions

  const rendreCompteDuResultat = (reponse) => {
    const ignores = reponse?.data?.ignores || [];
    const detail = ignores.length > 0
      ? ` Non modifiés : ${ignores.map((i) => `${i.nom} (${i.raison})`).join(', ')}.`
      : '';
    avertir(`${reponse.message}${detail}`, reponse?.data?.exigees?.length ? 'success' : 'warning');
  };

  const exigerPourUnCompte = async () => {
    if (!aExiger) return;
    setExigenceEnCours(true);
    const reponse = await api.doubleAuth.exiger({ employeIds: [aExiger.id] });
    setExigenceEnCours(false);
    if (reponse?.success) {
      setAExiger(null);
      rendreCompteDuResultat(reponse);
      await charger();
    } else {
      avertir(reponse?.message || "Impossible d'exiger la double authentification", 'error');
    }
  };

  const exigerPourUnGroupe = async () => {
    if (!groupeChoisi) return;
    setExigenceEnCours(true);
    const reponse = await api.doubleAuth.exiger({ groupeId: Number(groupeChoisi) });
    setExigenceEnCours(false);
    if (reponse?.success) {
      setDialogueGroupe(false);
      setGroupeChoisi('');
      rendreCompteDuResultat(reponse);
      await charger();
    } else {
      avertir(reponse?.message || "Impossible d'exiger la double authentification", 'error');
    }
  };

  const ouvrirRetrait = (compte, action) => {
    setRetrait({ compte, action });
    setMotif('');
  };

  const confirmerRetrait = async () => {
    if (!retrait || motif.trim().length < 5) return;
    setRetraitEnCours(true);
    const appel = retrait.action === 'reinitialiser'
      ? api.doubleAuth.reinitialiser
      : api.doubleAuth.desactiver;
    const reponse = await appel(retrait.compte.id, motif.trim());
    setRetraitEnCours(false);
    if (reponse?.success) {
      setRetrait(null);
      setMotif('');
      avertir(reponse.message);
      await charger();
    } else {
      avertir(reponse?.message || 'Opération impossible', 'error');
    }
  };

  // ------------------------------------------------------------ rendu

  if (chargement && comptes.length === 0 && !erreurChargement) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#246990]" />
          <p className="mt-4 text-sm text-gray-600">Chargement des comptes…</p>
        </div>
      </div>
    );
  }

  if (erreurChargement) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="mt-0.5 shrink-0 text-red-700" />
          <div className="min-w-0">
            <p className="font-semibold text-red-900">Les comptes n'ont pas pu être lus</p>
            <p className="mt-1 text-sm text-red-800">{erreurChargement}</p>
            <p className="mt-1 text-sm text-red-800">
              Rien n'est affiché plutôt qu'une liste vide : ne concluez pas qu'aucun compte n'est protégé.
            </p>
            <Button
              variant="outline"
              onClick={charger}
              className="mt-3 h-9 gap-2 border-red-300 text-red-800 hover:bg-red-100"
            >
              <RefreshCw size={15} />
              Réessayer
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const enRetraitReinit = retrait?.action === 'reinitialiser';

  return (
    <div className="space-y-4">

      <div className="flex items-start gap-3 rounded-lg border border-[#246990]/30 bg-[#246990]/5 p-4">
        <Info size={18} className="mt-0.5 shrink-0 text-[#246990]" />
        <div className="text-sm leading-relaxed text-gray-700">
          <p className="mb-1 font-medium text-gray-900">Comment ça fonctionne</p>
          <p>
            Vous <strong>exigez</strong> la double authentification pour un compte. À sa prochaine
            connexion, l'employé scanne un QR code avec une application d'authentification sur son
            téléphone ; ensuite, un code à six chiffres lui est demandé après son mot de passe.
          </p>
          <p className="mt-1.5 text-gray-600">
            Téléphone perdu ou changé : <strong>réinitialisez</strong>, il en enrôlera un nouveau.
            Vous ne pouvez pas retirer votre propre double authentification — demandez-le à un autre
            administrateur, ou au support KLASYS.
          </p>
        </div>
      </div>

      {/* ---------------- Tuiles ---------------- */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tuile libelle="Comptes" valeur={compteurs.total} icone={Users} ton="bg-[#246990]/10 text-[#246990]" />
        <Tuile libelle="Actives" valeur={compteurs.active} icone={ShieldCheck} ton="bg-green-100 text-green-700" />
        <Tuile libelle="En attente d'enrôlement" valeur={compteurs.en_attente} icone={Smartphone} ton="bg-amber-100 text-amber-700" />
        <Tuile libelle="Non exigées" valeur={compteurs.non_exigee} icone={ShieldOff} ton="bg-gray-100 text-gray-600" />
      </div>

      {/* ---------------- Comptes ---------------- */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Comptes de connexion</h3>
            <p className="text-xs text-gray-500">
              Les employés actifs qui ont un identifiant et un mot de passe.
            </p>
          </div>
          <button
            type="button"
            onClick={() => { setGroupeChoisi(''); setDialogueGroupe(true); }}
            className={`${BOUTON_SECTION} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
          >
            <Users size={14} />
            Exiger pour un groupe
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="Rechercher un compte…"
              className="h-9 w-[240px] rounded-lg border border-gray-200 pl-9 pr-3 text-sm focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15"
            />
          </div>

          <Select value={filtreEtat} onValueChange={setFiltreEtat}>
            <SelectTrigger className="h-9 w-[190px] border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990]/15">
              <SelectValue placeholder="Tous les états" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les états</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="en_attente">En attente</SelectItem>
              <SelectItem value="non_exigee">Non exigée</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filtreGroupe} onValueChange={setFiltreGroupe}>
            <SelectTrigger className="h-9 w-[210px] border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990]/15">
              <SelectValue placeholder="Tous les groupes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les groupes</SelectItem>
              {groupes.map((g) => (
                <SelectItem key={g.id} value={String(g.id)}>{g.nom}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <span className="ml-auto text-xs text-gray-500">
            {comptesFiltres.length} compte{comptesFiltres.length > 1 ? 's' : ''}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Compte</th>
                <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Groupe</th>
                <th className="w-[160px] px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">État</th>
                <th className="w-[210px] px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Depuis</th>
                <th className="w-[170px] px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Dernière connexion</th>
                <th className="w-[270px] px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {comptesFiltres.map((c, index) => {
                const etat = ETATS[c.etat] || ETATS.non_exigee;
                const estMoi = moi && Number(moi) === Number(c.id);
                return (
                  <tr
                    key={c.id}
                    className={`border-b border-gray-100 last:border-b-0 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                  >
                    <td className="px-5 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {nomComplet(c)}
                        {estMoi && <span className="ml-2 text-xs font-normal text-gray-500">(vous)</span>}
                      </div>
                      <div className="font-mono text-xs text-gray-500">{c.identifiant}</div>
                    </td>

                    <td className="px-5 py-3">
                      <div className="text-sm text-gray-700">{c.groupe_nom || '—'}</div>
                      {c.role_nom && <div className="text-xs text-gray-400">{c.role_nom}</div>}
                    </td>

                    <td className="px-5 py-3 text-center">
                      <span className={`inline-flex h-6 w-[132px] items-center justify-center whitespace-nowrap rounded-full text-xs font-medium ${etat.classes}`}>
                        {etat.libelle}
                      </span>
                    </td>

                    <td className="px-5 py-3">
                      {c.etat === 'active' && (
                        <div className="text-sm text-gray-700">Enrôlée le {jourEtHeure(c.enrolee_le)}</div>
                      )}
                      {c.etat !== 'non_exigee' && c.exigee_le && (
                        <div className="text-xs text-gray-400">
                          Exigée le {jourEtHeure(c.exigee_le)}{c.exigee_par ? ` par ${c.exigee_par}` : ''}
                        </div>
                      )}
                      {c.etat === 'non_exigee' && <span className="text-xs text-gray-300">—</span>}
                    </td>

                    <td className="px-5 py-3 text-sm text-gray-600">
                      {jourEtHeure(c.derniere_connexion) || <span className="text-xs text-gray-400">jamais</span>}
                    </td>

                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        {c.etat === 'non_exigee' && (
                          c.poste_de_pointage ? (
                            <span
                              className="inline-flex h-8 items-center text-xs text-gray-400"
                              title="Compte partagé par une tablette de pointage : un code bloquerait le pointage de tout le personnel."
                            >
                              Poste de pointage
                            </span>
                          ) : (
                            <Button
                              variant="outline"
                              onClick={() => setAExiger(c)}
                              className={`${BOUTON_LIGNE} text-[#246990] hover:border-[#246990]/40 hover:bg-[#246990]/5`}
                            >
                              <ShieldCheck size={14} />
                              Exiger
                            </Button>
                          )
                        )}

                        {c.etat !== 'non_exigee' && estMoi && (
                          <span
                            className="inline-flex h-8 items-center text-xs text-gray-400"
                            title="Un autre administrateur, ou le support KLASYS, peut le faire pour vous."
                          >
                            Votre compte
                          </span>
                        )}

                        {c.etat === 'active' && !estMoi && (
                          <Button
                            variant="outline"
                            onClick={() => ouvrirRetrait(c, 'reinitialiser')}
                            className={`${BOUTON_LIGNE} text-gray-700`}
                          >
                            <RotateCcw size={14} />
                            Réinitialiser
                          </Button>
                        )}

                        {c.etat !== 'non_exigee' && !estMoi && (
                          <Button
                            variant="outline"
                            onClick={() => ouvrirRetrait(c, 'desactiver')}
                            className={`${BOUTON_LIGNE} text-gray-600 hover:border-red-300 hover:text-red-700`}
                          >
                            <ShieldOff size={14} />
                            Désactiver
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}

              {comptesFiltres.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-sm text-gray-500">
                    {comptes.length === 0
                      ? "Aucun compte de connexion dans cet établissement."
                      : 'Aucun compte ne correspond aux filtres.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------------- Journal ---------------- */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
          <History size={16} className="text-[#246990]" />
          <h3 className="text-base font-semibold text-gray-900">Journal</h3>
          <span className="text-xs text-gray-500">— les 100 derniers événements</span>
        </div>

        {erreurJournal ? (
          <div className="flex items-start gap-3 p-4 text-sm text-red-800">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{erreurJournal} — le journal n'est pas vide, il n'a pas pu être lu.</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="w-[170px] px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Date</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Compte</th>
                  <th className="w-[160px] px-5 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Événement</th>
                  <th className="w-[140px] px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Origine</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Par</th>
                  <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Motif</th>
                </tr>
              </thead>
              <tbody>
                {journal.map((j, index) => {
                  const refus = j.action === 'code_refuse' || j.action === 'enrolement_refuse';
                  return (
                    <tr
                      key={j.id}
                      className={`border-b border-gray-100 last:border-b-0 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                    >
                      <td className="px-5 py-2.5 text-sm text-gray-600">{jourEtHeure(j.le)}</td>
                      <td className="px-5 py-2.5 text-sm text-gray-900">{j.employe_nom || `Employé #${j.employe_id}`}</td>
                      <td className="px-5 py-2.5 text-center">
                        <span className={`inline-flex h-6 w-[132px] items-center justify-center whitespace-nowrap rounded-full text-xs font-medium ${
                          refus ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {ACTIONS_JOURNAL[j.action] || j.action}
                        </span>
                      </td>
                      <td className="px-5 py-2.5 text-sm text-gray-600">{SOURCES_JOURNAL[j.source] || j.source}</td>
                      <td className="px-5 py-2.5 text-sm text-gray-600">{j.auteur_nom || '—'}</td>
                      <td className="px-5 py-2.5 text-sm text-gray-600">{j.motif || <span className="text-xs text-gray-300">—</span>}</td>
                    </tr>
                  );
                })}
                {journal.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-sm text-gray-500">
                      Aucun événement pour l'instant.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---------------- Exiger pour un compte ---------------- */}
      <ConfirmDialog
        isOpen={Boolean(aExiger)}
        onClose={() => setAExiger(null)}
        onConfirm={exigerPourUnCompte}
        isLoading={exigenceEnCours}
        type="info"
        title="Exiger la double authentification ?"
        description={
          aExiger
            ? `À sa prochaine connexion, ${nomComplet(aExiger)} devra scanner un QR code avec une application d'authentification sur son téléphone. Ses sessions ouvertes ne sont pas coupées.`
            : ''
        }
        confirmText="Exiger"
      />

      {/* ---------------- Exiger pour un groupe ---------------- */}
      <Dialog open={dialogueGroupe} onOpenChange={(ouvert) => { if (!exigenceEnCours) setDialogueGroupe(ouvert); }}>
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <Users size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Exiger la double authentification pour un groupe
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Chaque compte du groupe enrôlera son téléphone à sa prochaine connexion.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="space-y-3">
                <label className="block text-xs font-medium text-gray-700">Groupe de sécurité</label>
                <Select value={groupeChoisi} onValueChange={setGroupeChoisi}>
                  <SelectTrigger className="h-9 w-full border border-gray-200 bg-white focus:ring-2 focus:ring-[#246990]/15">
                    <SelectValue placeholder="Choisir un groupe" />
                  </SelectTrigger>
                  <SelectContent>
                    {groupes.map((g) => (
                      <SelectItem key={g.id} value={String(g.id)}>{g.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {apercuGroupe && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
                    <p>
                      <strong className="text-[#246990]">{apercuGroupe.aExiger}</strong> compte
                      {apercuGroupe.aExiger > 1 ? 's' : ''} passera{apercuGroupe.aExiger > 1 ? 'ont' : ''} en attente d'enrôlement.
                    </p>
                    {apercuGroupe.deja > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        {apercuGroupe.deja} l'ont déjà : ils ne sont pas touchés.
                      </p>
                    )}
                    {apercuGroupe.postes > 0 && (
                      <p className="mt-1 text-xs text-gray-500">
                        {apercuGroupe.postes} poste{apercuGroupe.postes > 1 ? 's' : ''} de pointage partagé
                        {apercuGroupe.postes > 1 ? 's' : ''} sera{apercuGroupe.postes > 1 ? 'ont' : ''} écarté{apercuGroupe.postes > 1 ? 's' : ''}.
                      </p>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      Aperçu d'après le groupe principal de chaque compte. Un employé rattaché au
                      groupe par un rôle secondaire est aussi concerné : le résultat le dira.
                    </p>
                  </div>
                )}
              </div>

              <div>
                <p className="mb-1.5 text-xs font-medium text-gray-700">Comptes du groupe</p>
                <div className="max-h-72 overflow-y-auto rounded-lg border border-gray-200">
                  {!apercuGroupe && (
                    <p className="px-3 py-8 text-center text-xs text-gray-500">Choisissez un groupe</p>
                  )}
                  {apercuGroupe && apercuGroupe.membres.length === 0 && (
                    <p className="px-3 py-8 text-center text-xs text-gray-500">
                      Aucun compte n'a ce groupe comme groupe principal.
                    </p>
                  )}
                  {apercuGroupe?.membres.map((c, index) => {
                    const etat = ETATS[c.etat] || ETATS.non_exigee;
                    return (
                      <div
                        key={c.id}
                        className={`flex items-center justify-between gap-3 px-3 py-2 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                      >
                        <div className="min-w-0">
                          <div className="truncate text-sm text-gray-900">{nomComplet(c)}</div>
                          <div className="font-mono text-[11px] text-gray-400">{c.identifiant}</div>
                        </div>
                        <span className={`inline-flex h-6 w-[132px] shrink-0 items-center justify-center whitespace-nowrap rounded-full text-xs font-medium ${
                          c.poste_de_pointage && c.etat === 'non_exigee' ? 'bg-gray-100 text-gray-400' : etat.classes
                        }`}>
                          {c.poste_de_pointage && c.etat === 'non_exigee' ? 'Poste de pointage' : etat.libelle}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED}
              onClick={() => setDialogueGroupe(false)}
              disabled={exigenceEnCours}
            >
              Annuler
            </Button>
            <Button
              onClick={exigerPourUnGroupe}
              disabled={!groupeChoisi || exigenceEnCours || (apercuGroupe && apercuGroupe.membres.length === 0)}
              className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            >
              {exigenceEnCours ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
              {exigenceEnCours ? 'Enregistrement…' : 'Exiger'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Réinitialiser / désactiver ---------------- */}
      <Dialog open={Boolean(retrait)} onOpenChange={(ouvert) => { if (!ouvert && !retraitEnCours) setRetrait(null); }}>
        <DialogContent className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              {enRetraitReinit ? <RotateCcw size={18} /> : <ShieldOff size={18} />}
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {enRetraitReinit ? 'Réinitialiser la double authentification' : 'Désactiver la double authentification'}
              </DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                {retrait ? `${nomComplet(retrait.compte)} · ${retrait.compte.identifiant}` : ''}
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="space-y-2 text-sm text-gray-700">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Ce qui va se passer</p>
                {enRetraitReinit ? (
                  <>
                    <p>Le téléphone actuellement enrôlé ne produira plus de code valide.</p>
                    <p>
                      La double authentification reste <strong>exigée</strong> : à sa prochaine connexion,
                      l'employé scannera un nouveau QR code.
                    </p>
                    <p className="text-gray-500">À utiliser pour un téléphone perdu, changé, ou une application supprimée.</p>
                  </>
                ) : (
                  <>
                    <p>Le téléphone enrôlé est oublié.</p>
                    <p>
                      Le compte se connectera de nouveau avec son <strong>mot de passe seul</strong>,
                      jusqu'à ce que la double authentification soit exigée à nouveau.
                    </p>
                  </>
                )}
                <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs text-gray-600">
                  <KeyRound size={13} className="mt-0.5 shrink-0 text-gray-400" />
                  Le geste et son motif sont écrits au journal, avec votre nom.
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-medium text-gray-700">
                  Motif <span className="text-red-600">*</span>
                </label>
                <textarea
                  value={motif}
                  onChange={(e) => setMotif(e.target.value)}
                  rows={5}
                  placeholder={enRetraitReinit ? 'Ex. : téléphone perdu, signalé le 15/09' : 'Pourquoi retirer cette protection ?'}
                  className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/15"
                />
                <p className="mt-1 text-[11px] text-gray-500">Au moins 5 caractères.</p>
              </div>
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              variant="outline"
              className={BOUTON_PIED}
              onClick={() => setRetrait(null)}
              disabled={retraitEnCours}
            >
              Annuler
            </Button>
            <Button
              onClick={confirmerRetrait}
              disabled={motif.trim().length < 5 || retraitEnCours}
              className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            >
              {retraitEnCours
                ? <Loader2 size={16} className="animate-spin" />
                : (enRetraitReinit ? <RotateCcw size={16} /> : <ShieldOff size={16} />)}
              {retraitEnCours ? 'Enregistrement…' : (enRetraitReinit ? 'Réinitialiser' : 'Désactiver')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default DoubleAuthManager;
